import { useCallback, useEffect, useRef, useState } from "react";

export interface UseKgNodePanelTabsScrollResult {
  scrollRef: React.RefObject<HTMLDivElement>;
  tabRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  setTabRef: (tabId: string) => (element: HTMLButtonElement | null) => void;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  onScroll: () => void;
}

export const useKgNodePanelTabsScroll = (
  activeTab: string,
): UseKgNodePanelTabsScrollResult => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollEdges = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = element;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const onScroll = useCallback(() => {
    updateScrollEdges();
  }, [updateScrollEdges]);

  const setTabRef = useCallback(
    (tabId: string) => (element: HTMLButtonElement | null) => {
      tabRefs.current[tabId] = element;
    },
    [],
  );

  useEffect(() => {
    updateScrollEdges();
    const element = scrollRef.current;
    if (!element) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      updateScrollEdges();
    });
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateScrollEdges]);

  useEffect(() => {
    const activeButton = tabRefs.current[activeTab];
    activeButton?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
    requestAnimationFrame(updateScrollEdges);
  }, [activeTab, updateScrollEdges]);

  return {
    scrollRef,
    tabRefs,
    setTabRef,
    canScrollLeft,
    canScrollRight,
    onScroll,
  };
};
